import { Fragment, useMemo } from 'react';

import { CtaEventDataType } from '@zola-helpers/client/dist/es/tracking/ctaEvents/ctaEvents';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { WeddingCardView } from '@zola/svc-marketplace-ts-types';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import H from '@zola/zola-ui/src/typography/Headings';

import { CardShelf } from '~/components/common/cards/CardShelf/CardShelf';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import WeddingAlbumCard from '~/pages/weddings/components/WeddingAlbumCard/WeddingAlbumCard';
import { LocationOrAllMarketsType, WeddingOptionFacetView } from '~/types/responseTypes';
import {
  CtaType,
  TrackingProductLocation,
  TrackingProductType,
  trackProductClicked,
} from '~/util/trackingHelper';

import { trackExploreVendorsCTA } from '../../../../util/tracking';
import { PageSection } from '../../../PageSection/PageSection';

import styles from './realWeddings.module.less';

const handleClick = (wedding: WeddingCardView, position: number) => {
  trackProductClicked({
    location: TrackingProductLocation.EXPLORE_VENDORS_PLP,
    name: wedding.title || '',
    productId: wedding.uuid || '',
    position,
    productType: TrackingProductType.WEDDING_ALBUM_CARD,
  });
};

export interface RealWeddingsProps {
  selectedLocation?: LocationOrAllMarketsType | null;
}

export const RealWeddings = ({ className }: { className?: string }) => {
  const {
    realWeddings: weddings,
    vendorSearch: { selectedLocation },
  } = useSearchResultsPageContext();

  const CTA: CtaEventDataType = useMemo(() => {
    const base = {
      location: TrackingProductLocation.SEARCH_RESULTS_REAL_WEDDINGS,
      cta_type: CtaType.LINK,
      position: 1,
      section: 'REAL_WEDDINGS',
      business_unit: BUSINESS_UNIT.MARKETPLACE,
    };
    const { type, label, slug } = selectedLocation || {};

    if (type === 'CITY' && label && slug) {
      return {
        ...base,
        cta_id: `View all real weddings in ${label}`,
        cta: `/inspiration/real-weddings/${slug}`,
      };
    }
    return {
      ...base,
      cta_id: 'View all real weddings',
      cta: '/inspiration',
    };
  }, [selectedLocation]);

  const hed = useMemo(() => {
    const { type, label } = selectedLocation || {};
    if (type === 'CITY' && label) {
      return `Get inspired by real weddings in ${label}`;
    }
    return 'Get inspired by real weddings';
  }, [selectedLocation]);

  if (!weddings.length) {
    return <Fragment />;
  }

  return (
    <PageSection className={className}>
      <div className={styles.hed}>
        <H.Title2 presentation="h3">{hed}</H.Title2>
        <LinkV2
          className="real-weddings__see-all"
          href={CTA.cta || '#'}
          onClick={() => trackExploreVendorsCTA(CTA)}
          arrow
          noTextTransform
        >
          {CTA.cta_id}
        </LinkV2>
      </div>
      <CardShelf hoverScaleAdjust={false}>
        {weddings.map((wedding, index) => {
          const { colors, title, slug, uuid } = wedding;
          return (
            <WeddingAlbumCard
              className={styles.realWeddingCard}
              trackClicked={() => handleClick(wedding, index + 1)}
              colors={(colors as unknown as WeddingOptionFacetView[]) || []}
              imageUuid={wedding?.photos[0]?.imageId || ''}
              title={title || ''}
              slug={slug || ''}
              trackingLocation={TrackingProductLocation.EXPLORE_VENDORS_PLP}
              uuid={uuid || ''}
              index={index}
              carousel
              key={`wedding-${wedding.uuid}`}
              lazyLoad
            />
          );
        })}
      </CardShelf>
    </PageSection>
  );
};
